/**
 * Programs data mapper - transforms API response to UI model
 */

import { getDictValue } from '@/lib/utils/dictMapper';

export interface ProgramImage {
  src: string;
  alt: string;
  placeholder: string;
}

export interface Program {
  id: number;
  title: string;
  image: ProgramImage;
  description: string;
  category: string;
  status: string;
  duration: string;
  participants: string;
}

export interface ProgramsLayout {
  itemsPerView: number;
  spacing: number;
  aspectRatio: string;
  cardStyle: string;
  showHoverEffect: boolean;
  showGradient: boolean;
}

export interface ProgramsStyling {
  backgroundColor: string;
  badgeColor: string;
  titleColor: string;
  cardBackgroundColor: string;
  overlayColor: string;
  gradientFrom: string;
  gradientTo: string;
  textColor: string;
  descriptionColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  buttonHoverBackgroundColor: string;
  buttonHoverTextColor: string;
}

export interface ProgramsCTA {
  text: string;
  variant: string;
  size: string;
  showIcon: boolean;
}

export interface ProgramsModel {
  title: string;
  subtitle: string;
  badge: string;
  programs: Program[];
  layout: ProgramsLayout;
  styling: ProgramsStyling;
  cta: ProgramsCTA;
}

// Real backend API response structure
export interface BackendService {
  id: string;
  name: string;
  key: string;
  description: string;
  image?: string; // Optional image field from backend
  showOnSite: boolean;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BackendServicesResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: BackendService[];
  meta: {
    requestId: string;
    timestamp: string;
    duration: number;
    pagination: {
      page: number;
      limit: string;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    path: string;
    method: string;
    userAgent: string;
    ip: string;
  };
}

// Legacy API response (for fallback)
export interface ProgramsApiResponse {
  title: string;
  subtitle: string;
  badge: string;
  programs: Program[];
  layout: ProgramsLayout;
  styling: ProgramsStyling;
  cta: ProgramsCTA;
}

/**
 * Maps backend API response to Programs UI model
 */
export function mapProgramsFromBackend(backendData: BackendServicesResponse, dict: any): ProgramsModel {
  console.log('🔍 [Programs Mapper] Starting backend mapping...');
  console.log('🔍 [Programs Mapper] Backend data:', {
    success: backendData.success,
    statusCode: backendData.statusCode,
    dataLength: backendData.data?.length || 0,
    services: backendData.data?.map(s => ({ name: s.name, key: s.key, hasImage: !!s.image })) || []
  });
  
  // Filter active services that should be shown on site
  const activeServices = backendData.data.filter(service => service.isActive && service.showOnSite && !service.isDeleted);
  
  console.log('🔍 [Programs Mapper] Active services after filtering:', {
    total: activeServices.length,
    services: activeServices.map(s => ({ name: s.name, key: s.key, hasImage: !!s.image }))
  });
  
  // Map backend services to UI programs
  const programs: Program[] = activeServices.map((backendService, index) => {
    const imageSrc = backendService.image || getServiceImage(backendService.key);
    const program = {
      id: index + 1, // Use index as ID for UI
      title: backendService.name,
      image: {
        src: imageSrc,
        alt: backendService.name,
        placeholder: imageSrc
      },
      description: backendService.description || '',
      category: getServiceCategory(backendService.key),
      status: 'available',
      duration: getServiceDuration(backendService.key),
      participants: getServiceParticipants(backendService.key)
    };
    
    console.log(`🔍 [Programs Mapper] Mapped program ${index + 1}:`, {
      title: program.title,
      imageSrc: program.image.src,
      category: program.category,
      hasBackendImage: !!backendService.image
    });
    
    return program;
  });

  return {
    title: getDictValue(dict, 'programs_title'),
    subtitle: getDictValue(dict, 'programs_subtitle'),
    badge: getDictValue(dict, 'programs_badge'),
    programs,
    layout: {
      itemsPerView: 3,
      spacing: 16,
      aspectRatio: '16:9',
      cardStyle: 'modern',
      showHoverEffect: true,
      showGradient: true
    },
    styling: {
      backgroundColor: 'bg-gray-50',
      badgeColor: 'text-blue-600',
      titleColor: 'text-gray-900',
      cardBackgroundColor: 'bg-white',
      overlayColor: 'bg-black/20',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-purple-600',
      textColor: 'text-gray-900',
      descriptionColor: 'text-gray-600',
      buttonBackgroundColor: 'bg-blue-600',
      buttonTextColor: 'text-white',
      buttonHoverBackgroundColor: 'hover:bg-blue-700',
      buttonHoverTextColor: 'hover:text-white'
    },
    cta: {
      text: getDictValue(dict, 'programs_viewAll'),
      variant: 'primary',
      size: 'md',
      showIcon: true
    }
  };
}

/**
 * Gets appropriate image for service based on key
 */
function getServiceImage(serviceKey: string): string {
  const imageMap: { [key: string]: string } = {
    'assisted-living': 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/services/assisted-living.jpg',
    'memory-care': 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/services/memory-care.jpg',
    'independent-living': 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/services/independent-living.jpg',
    'home-care': 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/services/home-care.jpg',
    'respite-care': 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/services/respite-care.jpg'
  };
  
  return imageMap[serviceKey] || 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/services/default-service.jpg';
}

/**
 * Gets category for service based on key
 */
function getServiceCategory(serviceKey: string): string {
  const categoryMap: { [key: string]: string } = {
    'assisted-living': 'Healthcare',
    'memory-care': 'Healthcare',
    'independent-living': 'Lifestyle',
    'home-care': 'Healthcare',
    'respite-care': 'Healthcare'
  };
  
  return categoryMap[serviceKey] || 'General';
}

/**
 * Gets duration for service based on key
 */
function getServiceDuration(serviceKey: string): string {
  const durationMap: { [key: string]: string } = {
    'assisted-living': '24/7 Care',
    'memory-care': 'Specialized Care',
    'independent-living': 'Flexible',
    'home-care': 'As Needed',
    'respite-care': 'Short-term'
  };
  
  return durationMap[serviceKey] || 'Varies';
}

/**
 * Gets participants for service based on key
 */
function getServiceParticipants(serviceKey: string): string {
  const participantsMap: { [key: string]: string } = {
    'assisted-living': 'Seniors 65+',
    'memory-care': 'Memory Care Residents',
    'independent-living': 'Active Seniors',
    'home-care': 'All Ages',
    'respite-care': 'Family Caregivers'
  };
  
  return participantsMap[serviceKey] || 'All Ages';
}

/**
 * Maps API response to Programs UI model (legacy)
 */
export function mapProgramsFromApi(apiData: ProgramsApiResponse): ProgramsModel {
  return {
    title: apiData.title,
    subtitle: apiData.subtitle,
    badge: apiData.badge,
    programs: apiData.programs.map(program => ({
      id: program.id,
      title: program.title,
      image: {
        src: program.image.src,
        alt: program.image.alt,
        placeholder: program.image.placeholder
      },
      description: program.description,
      category: program.category,
      status: program.status,
      duration: program.duration,
      participants: program.participants
    })),
    layout: {
      itemsPerView: apiData.layout.itemsPerView,
      spacing: apiData.layout.spacing,
      aspectRatio: apiData.layout.aspectRatio,
      cardStyle: apiData.layout.cardStyle,
      showHoverEffect: apiData.layout.showHoverEffect,
      showGradient: apiData.layout.showGradient
    },
    styling: {
      backgroundColor: apiData.styling.backgroundColor,
      badgeColor: apiData.styling.badgeColor,
      titleColor: apiData.styling.titleColor,
      cardBackgroundColor: apiData.styling.cardBackgroundColor,
      overlayColor: apiData.styling.overlayColor,
      gradientFrom: apiData.styling.gradientFrom,
      gradientTo: apiData.styling.gradientTo,
      textColor: apiData.styling.textColor,
      descriptionColor: apiData.styling.descriptionColor,
      buttonBackgroundColor: apiData.styling.buttonBackgroundColor,
      buttonTextColor: apiData.styling.buttonTextColor,
      buttonHoverBackgroundColor: apiData.styling.buttonHoverBackgroundColor,
      buttonHoverTextColor: apiData.styling.buttonHoverTextColor
    },
    cta: {
      text: apiData.cta.text,
      variant: apiData.cta.variant,
      size: apiData.cta.size,
      showIcon: apiData.cta.showIcon
    }
  };
}

/**
 * Maps API response to Programs UI model with dictionary translation
 */
export function mapProgramsFromApiWithDict(apiData: ProgramsApiResponse, dict: any): ProgramsModel {
  const mapped = mapProgramsFromApi(apiData);
  
  // Map dictionary keys to actual values
  return {
    title: getDictValue(dict, mapped.title),
    subtitle: getDictValue(dict, mapped.subtitle),
    badge: mapped.badge,
    programs: mapped.programs.map(program => ({
      id: program.id,
      title: program.title, // Keep program titles as they are (not translated)
      image: {
        src: program.image.src,
        alt: program.image.alt,
        placeholder: program.image.placeholder
      },
      description: program.description, // Keep descriptions as they are (not translated)
      category: program.category,
      status: program.status,
      duration: program.duration,
      participants: program.participants
    })),
    layout: mapped.layout,
    styling: mapped.styling,
    cta: {
      text: getDictValue(dict, mapped.cta.text),
      variant: mapped.cta.variant,
      size: mapped.cta.size,
      showIcon: mapped.cta.showIcon
    }
  };
}
